import { useState } from 'react';

import type { RelatedClientRecord } from '@/apps/engagement-letter-database/data/types';

import { Check as CheckIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Box, Divider, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';

interface RelatedClientInfoPaneProps {
  record: RelatedClientRecord;
}

const RelatedClientInfoPane = ({ record }: RelatedClientInfoPaneProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const columns = [
      { label: 'Related Client Number', value: record.RelatedClientNumber ?? 'Not available' },
      { label: 'Related Client Name', value: record.RelatedClientName ?? 'Not available' },
      { label: 'Associated Client(s)', value: String(record.Clients.length) },
    ];

    const escapeHtml = (value: string) =>
      value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

    const headerCells = columns
      .map(
        (col) =>
          `<th style="border:1px solid #999;padding:6px 10px;background-color:#f2f2f2;text-align:left;font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(
            col.label,
          )}</th>`,
      )
      .join('');
    const valueCells = columns
      .map(
        (col) =>
          `<td style="border:1px solid #999;padding:6px 10px;text-align:left;font-family:Calibri,Arial,sans-serif;font-size:11pt;">${escapeHtml(
            col.value,
          )}</td>`,
      )
      .join('');
    const html = `<table style="border-collapse:collapse;"><thead><tr>${headerCells}</tr></thead><tbody><tr>${valueCells}</tr></tbody></table>`;
    const plainText = `${columns.map((col) => col.label).join('\t')}\n${columns.map((col) => col.value).join('\t')}`;

    try {
      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy related client information:', error);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" gutterBottom>
          Related Client Information
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy as table'}>
          <IconButton
            onClick={() => void handleCopy()}
            size="small"
            aria-label="Copy related client information as table"
          >
            {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={3}>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Related Client Number
          </Typography>
          <Typography variant="body1">{record.RelatedClientNumber ?? 'Not available'}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Related Client Name
          </Typography>
          <Typography variant="body1">{record.RelatedClientName ?? 'Not available'}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption" color="text.secondary">
            Associated Client(s)
          </Typography>
          <Typography variant="body1">{record.Clients.length}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default RelatedClientInfoPane;
